import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Button,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Refresh as RefreshIcon, Error as ErrorIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import UserManager from '../components/UserManager';
import SourceManager from '../components/SourceManager';
import LLMConfig from '../components/LLMConfig';
import api from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface ScrapingLog {
  id: string;
  source_id: string;
  source_name: string;
  source_url: string | null;
  status: string;
  error_message: string | null;
  articles_found: number | null;
  started_at: string;
  completed_at: string | null;
  duration_seconds: number | null;
}

const AdminPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const [logsPageSize, setLogsPageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [selectedError, setSelectedError] = useState<string>('');
  const queryClient = useQueryClient();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Fetch scraping logs
  const { data: logsData, isLoading: logsLoading } = useQuery({
    queryKey: ['scraping-logs', logsPage + 1, logsPageSize, statusFilter],
    queryFn: async () => {
      const params: any = {
        page: logsPage + 1,
        page_size: logsPageSize,
      };
      if (statusFilter) {
        params.status = statusFilter;
      }
      const response = await api.get('/api/admin/scraping/logs', { params });
      return response.data;
    },
    enabled: tabValue === 2, // Only fetch when Logs tab is active
  });

  // Trigger manual scraping mutation
  const triggerScrapingMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/api/admin/scraping/trigger');
      return response.data;
    },
    onSuccess: (data: any) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['scraping-logs'] });
        setError(null);
      } else {
        setError(data.error?.message || 'Failed to trigger scraping');
      }
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to trigger scraping');
    },
  });

  const handleTriggerScraping = () => {
    setError(null);
    triggerScrapingMutation.mutate();
  };

  const handleLogsPageChange = (event: unknown, newPage: number) => {
    setLogsPage(newPage);
  };

  const handleLogsPageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLogsPageSize(parseInt(event.target.value, 10));
    setLogsPage(0);
  };

  const handleOpenErrorDialog = (errorMessage: string) => {
    setSelectedError(errorMessage);
    setErrorDialogOpen(true);
  };

  const handleCloseErrorDialog = () => {
    setErrorDialogOpen(false);
    setSelectedError('');
  };

  const logs: ScrapingLog[] = logsData?.data?.items || [];
  const logsTotal = logsData?.data?.total || 0;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
        return 'info';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (seconds === null) return 'N/A';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <Layout>
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Typography variant="h4" gutterBottom>
            Administration
          </Typography>

          <Paper sx={{ mt: 3 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="admin tabs"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Sources" id="admin-tab-0" aria-controls="admin-tabpanel-0" />
              <Tab label="Utilisateurs" id="admin-tab-1" aria-controls="admin-tabpanel-1" />
              <Tab label="Logs" id="admin-tab-2" aria-controls="admin-tabpanel-2" />
              <Tab label="Configuration LLM" id="admin-tab-3" aria-controls="admin-tabpanel-3" />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              <SourceManager />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <UserManager />
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5">Scraping Logs</Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Status Filter</InputLabel>
                      <Select
                        value={statusFilter}
                        label="Status Filter"
                        onChange={(e) => {
                          setStatusFilter(e.target.value);
                          setLogsPage(0);
                        }}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="success">Success</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                        <MenuItem value="running">Running</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      startIcon={<RefreshIcon />}
                      onClick={handleTriggerScraping}
                      disabled={triggerScrapingMutation.isPending}
                    >
                      Trigger Scraping
                    </Button>
                  </Box>
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                {triggerScrapingMutation.isSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }} onClose={() => triggerScrapingMutation.reset()}>
                    Scraping triggered successfully! Results:{' '}
                    {triggerScrapingMutation.data?.data?.results?.successful || 0}/
                    {triggerScrapingMutation.data?.data?.results?.total_sources || 0} sources successful,{' '}
                    {triggerScrapingMutation.data?.data?.results?.total_articles || 0} articles found
                  </Alert>
                )}

                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Source</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Articles Found</TableCell>
                        <TableCell>Started At</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {logsLoading ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            Loading...
                          </TableCell>
                        </TableRow>
                      ) : logs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No scraping logs found. Trigger a scraping to see results.
                          </TableCell>
                        </TableRow>
                      ) : (
                        logs.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {log.source_name}
                                </Typography>
                                {log.source_url && (
                                  <Typography variant="caption" color="text.secondary">
                                    {log.source_url}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={log.status}
                                color={getStatusColor(log.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {log.articles_found !== null ? log.articles_found : 'N/A'}
                            </TableCell>
                            <TableCell>
                              {new Date(log.started_at).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              {formatDuration(log.duration_seconds)}
                            </TableCell>
                            <TableCell>
                              {log.error_message ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{
                                      maxWidth: 250,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {log.error_message}
                                  </Typography>
                                  <Tooltip title="Voir le message complet">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleOpenErrorDialog(log.error_message!)}
                                    >
                                      <ErrorIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={logsTotal}
                    page={logsPage}
                    onPageChange={handleLogsPageChange}
                    rowsPerPage={logsPageSize}
                    onRowsPerPageChange={handleLogsPageSizeChange}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                  />
                </TableContainer>
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <LLMConfig />
            </TabPanel>
          </Paper>
        </Box>
      </Container>

      {/* Dialog pour afficher le message d'erreur complet */}
      <Dialog
        open={errorDialogOpen}
        onClose={handleCloseErrorDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ErrorIcon color="error" />
            <Typography variant="h6">Message d'erreur</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: 2,
              borderRadius: 1,
            }}
          >
            {selectedError}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseErrorDialog}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default AdminPage;
